// Basemap catalog + resolution. Pure module — the MapLayersControl component
// renders it, MapView consumes getInitialMapStyle at map creation.
import { Map, Satellite, Moon, Layers, Globe, SatelliteDish } from 'lucide-react';
import { applyMapDetail, getMapDetail } from './mapDetail';

export const STORAGE_KEY = 'trackizy_map_style';
const LIGHT_TILES = 'https://tiles.openfreemap.org/styles/liberty';
const DARK_TILES = 'https://tiles.openfreemap.org/styles/dark';
export const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

// Mapbox Studio styles like `streets-v12` reference their tile sources via
// the proprietary `mapbox://` protocol (e.g. `mapbox://mapbox.mapbox-streets-v8`).
// MapLibre doesn't resolve that protocol by default, so pointing it at the
// style.json renders nothing — the sources never fetch. The fix is to use
// Mapbox's raster-tile endpoint, which serves the same Studio-rendered map
// as plain raster tiles MapLibre can fetch directly. Slightly heavier than
// vector but identical visually and renders correctly out of the box.
const MAPBOX_STREETS = MAPBOX_TOKEN
  ? {
      version: 8,
      sources: {
        'mapbox-streets': {
          type: 'raster',
          tiles: [
            `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
          ],
          tileSize: 256,
          attribution: '© Mapbox © OpenStreetMap',
          maxzoom: 22,
        },
      },
      layers: [{ id: 'mapbox-streets-layer', type: 'raster', source: 'mapbox-streets' }],
      glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    }
  : null;

/**
 * MapLibre `transformStyle` hook applied to EVERY basemap we load (initial
 * style + every switch in MapLayersControl). OpenFreeMap's Liberty style
 * paints a Natural Earth hillshade raster (`ne2_shaded`) under zoom 7 — twelve
 * 100–350 KB PNGs (~2.7 MB) for the default zoom-6 first view, on top of the
 * 1.3 MB of vector tiles, which is why the Live Map painted seconds after a
 * raster-tile competitor on Iraqi links (2026-10-26). Drop the source and
 * any layer drawing it; the terrain tint is decorative. Styles without that
 * source pass through unchanged.
 */
export function slimBasemapStyle(_previousStyle, nextStyle) {
  const sources = nextStyle?.sources;
  if (!sources || !Object.prototype.hasOwnProperty.call(sources, 'ne2_shaded')) return nextStyle;
  const { ne2_shaded: _dropped, ...keptSources } = sources;
  return {
    ...nextStyle,
    sources: keptSources,
    layers: (nextStyle.layers || []).filter((layer) => layer.source !== 'ne2_shaded'),
  };
}

/**
 * The full `transformStyle`: drop the hillshade, then apply the user's Map
 * detail level (Less / Normal / More — src/features/map/mapDetail.js) and UI
 * language, both read at the moment the style loads so a re-`setStyle` picks
 * up a change.
 */
export function transformBasemap(previousStyle, nextStyle) {
  return applyMapDetail(slimBasemapStyle(previousStyle, nextStyle), getMapDetail(), { lang: uiLanguage() });
}

/** UI language for Map detail's quarter names. src/lib/i18n.js stores it under
 *  `language` only; `i18nextLng` is never read (a stale one from an older build
 *  gave an English UI Arabic-first names). Then `<html lang>`, then English. */
function uiLanguage() {
  let stored = null;
  try {
    stored = localStorage.getItem('language');
  } catch {
    // storage blocked — fall through
  }
  return stored || (typeof document !== 'undefined' && document.documentElement?.lang) || 'en';
}

/** Pass as the options arg of `map.setStyle(style, BASEMAP_STYLE_OPTIONS)`. */
export const BASEMAP_STYLE_OPTIONS = { transformStyle: transformBasemap };

/** Read the current app theme from the <html> dark class. */
function isAppDark() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/** Theme-aware default tile style — light tiles when app is light, dark when dark. */
export function defaultTileStyle() {
  return isAppDark() ? DARK_TILES : LIGHT_TILES;
}

const SATELLITE_STYLE = {
  version: 8,
  name: 'ESRI World Imagery',
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Tiles &copy; Esri',
      maxzoom: 19,
    },
  },
  layers: [
    {
      id: 'esri-satellite-layer',
      type: 'raster',
      source: 'esri-satellite',
      minzoom: 0,
      maxzoom: 22,
    },
  ],
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
};

// Hybrid — Esri imagery + reference overlays. Same pattern as SATELLITE_STYLE
// with two extra raster sources stacked on top (order = draw order).
export const HYBRID_STYLE = {
  version: 8,
  name: 'ESRI Hybrid',
  sources: {
    'esri-imagery': {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256, attribution: 'Tiles &copy; Esri', maxzoom: 19,
    },
    'esri-transport': {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256, maxzoom: 19,
    },
    'esri-labels': {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256, maxzoom: 19,
    },
  },
  layers: [
    { id: 'esri-imagery-layer', type: 'raster', source: 'esri-imagery' },
    { id: 'esri-transport-layer', type: 'raster', source: 'esri-transport' },
    { id: 'esri-labels-layer', type: 'raster', source: 'esri-labels' },
  ],
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
};

const BASE_MAP_STYLES = [
  { id: 'default',   labelKey: 'mapStyle.default',   fallback: 'Default',        icon: Map,           style: LIGHT_TILES },
  { id: 'hybrid',    labelKey: 'mapStyle.hybrid',    fallback: 'Hybrid',         icon: SatelliteDish, style: HYBRID_STYLE },
  { id: 'mapbox',    labelKey: 'mapStyle.mapbox',    fallback: 'Mapbox Streets', icon: Layers,        style: MAPBOX_STREETS, requiresToken: !MAPBOX_TOKEN },
  { id: 'satellite', labelKey: 'mapStyle.satellite', fallback: 'Satellite',      icon: Satellite,     style: SATELLITE_STYLE },
  { id: 'dark',      labelKey: 'mapStyle.dark',      fallback: 'Dark',           icon: Moon,          style: DARK_TILES },
];

export const BUILTIN_IDS = BASE_MAP_STYLES.map((s) => s.id);

/** Org customs list, tolerating the legacy single-object shape. */
export function normalizeCustoms(orgBasemap) {
  if (Array.isArray(orgBasemap?.customs)) {
    return orgBasemap.customs.filter((c) => c?.styleUrl);
  }
  const legacy = orgBasemap?.custom;
  if (legacy?.styleUrl) return [{ id: 'custom', name: legacy.name, styleUrl: legacy.styleUrl }];
  return [];
}

/**
 * The picker catalog: built-ins (minus token-gated, minus org-hidden — but
 * 'default' is unhideable) + org customs + platform basemaps.
 */
export function getMapStyles(orgBasemap, platformBasemaps) {
  const hidden = new Set((orgBasemap?.hiddenBuiltins || []).filter((id) => id !== 'default'));
  const entries = BASE_MAP_STYLES.filter((s) => !s.requiresToken && !hidden.has(s.id));
  for (const c of normalizeCustoms(orgBasemap)) {
    entries.push({ id: c.id, fallback: c.name || 'Custom', icon: Globe, style: c.styleUrl });
  }
  for (const p of Array.isArray(platformBasemaps) ? platformBasemaps : []) {
    if (!p?.styleUrl) continue;
    entries.push({ id: p.id, fallback: p.name || 'Platform', icon: Globe, style: p.styleUrl });
  }
  return entries;
}

/**
 * Initial style for MapView. Precedence: user localStorage pick → org default
 * → theme-aware default. Stale picks fall through (styles.find misses).
 */
export function getInitialMapStyle(opts) {
  const styles = getMapStyles(opts?.orgBasemap, opts?.platformBasemaps);
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const entry = styles.find((s) => s.id === saved);
      if (entry?.id === 'default') return defaultTileStyle();
      if (entry) return entry.style;
    }
  } catch { /* localStorage unavailable */ }
  if (opts?.orgDefault) {
    const entry = styles.find((s) => s.id === opts.orgDefault);
    if (entry?.id === 'default') return defaultTileStyle();
    if (entry) return entry.style;
  }
  return defaultTileStyle();
}
